
import { VueConstructor } from 'vue';
import { NavigationGuard, RawLocation, Route, RouteConfig } from 'vue-router';
import { RouteConfigSingleView } from 'vue-router/types/router';
import { PageMode } from '@/main/utils/pageMode';
import { editName, listName, selectListName } from '@/main/store/listEditTypes';
import { router } from '@/main/router';
import EmptyRouterView from '@/main/router/EmptyRouterView.vue';
import NotFound from '@/main/pages/NotFound.vue';
//import AccessDenied from '@/main/pages/AccessDenied.vue';
import { ListLoadBehavior } from '@/main/utils/listLoadBehavior';

type Components = {
	List?: VueConstructor
	SelectList?: VueConstructor
	Edit?: VueConstructor,
	Default?: VueConstructor
};

function isComponents(x: object): x is Components {
	return Object.keys(x).every(
		(key) => ['List', 'SelectList', 'Edit', 'Default'].includes(key),
	);
}

export class Path implements RouteConfigSingleView {
	beforeEnter?: NavigationGuard;
	component?: VueConstructor;
	meta?: { objectName: string }; // TODO: remove
	children: RouteConfig[] = [];

	constructor(
		public path: string = '',
		components: VueConstructor | Components = EmptyRouterView,
		meta: string | null = null, // TODO: remove last 2 parameters
		public name?: string,
	) {
		// eslint-disable-next-line no-param-reassign
		if (!isComponents(components)) components = { Default: components };

		const children = [];

		if ('List' in components) {
			children.push({
				path: `:mode(${PageMode.LIST})`,
				component: components.List,
				meta: { objectName: path },
				name: listName(path),
				props: true,
			});
		}

		if ('SelectList' in components) {
			children.push({
				path: `:mode(${PageMode.SELECT_LIST})`,
				component: components.SelectList,
				meta: { objectName: path },
				name: selectListName(path),
				props: true,
			});
		}

		if ('Edit' in components) {
			children.push(editPath(path, components.Edit!));
		}

		children.length > 0 && this.addChildren(children, 'list' in components);

		this.component = components.Default || EmptyRouterView;
	}

	addChildren(children: RouteConfig[], hasList: boolean = false) {
		const last = this.children.pop() || { path: '', component: NotFound };

		this.children.push(...children, last);
		// const secondlast = this.children.pop() || { path: 'accessdenied', component: AccessDenied };

		// this.children.push(...children, last, secondlast);

		if (hasList) {
			if (last.meta) last.meta.hasList = true; else last.meta = { hasList: true };
			//if (secondlast.meta) secondlast.meta.hasList = true; else secondlast.meta = { hasList: true };
		}

		return this;
	}
}


export function editPath(path: string, component: VueConstructor) {
	return {
		path: `:mode(${PageMode.CREATE}|${PageMode.EDIT}|${PageMode.VIEW})/:id?`,
		component,
		meta: { objectName: path },
		name: editName(path),
		props: true,
		beforeEnter(to: Route, from: Route, next: (path?: any) => any): void {
			if ((to.params.mode === PageMode.CREATE) !== (to.params.id === undefined)) {
				return next({ path: '/404', replace: true });
			}

			return next();
		},
	};
}

/**
 * Opens new SelectList component.
 *
 * @param name - name of the component, eg. 'Vozidlo'.
 * @param args
 */
export function openSelectList(name: string, args?: {
	afterRowSelection?: (...arg: any) => any,
	filter?: object,
	listLoadBehavior?: ListLoadBehavior,
	[key: string]: any,
}): Promise<Route> {
	return router.push({
		name: selectListName(name),
		params: {
			/* TODO: Vue Router requires all params to be strings that are part of the
			 *   URL, but currently we are passing all sorts of data.
			 *   This extra data is not remembered when another page is visited and
			 *   user goes back in history.
			 *   It should ideally be passed using a Vuex mutation/action. Same for
			 *   openList and openEdit.
			 */
			...args as Record<string, string>,
			mode: PageMode.SELECT_LIST,
			destroy: 'TODO: is this arg necessary?',
		},
	});
}

/**
 * Opens new List component.
 *
 * @param name - name of the component, eg. 'Vozidlo'.
 * @param args
 */
export function openList(name: string, args?: {
	filter?: object,
	listLoadBehavior?: ListLoadBehavior,
	[key: string]: any,
}, query?: object) {
	if (query) {
		return router.push({
			name: listName(name),
			params: { ...args as any, mode: PageMode.LIST, destroy: 'TODO: is this arg necessary?' },
			query: { ...query },
		});
	}
	return router.push({
		name: listName(name),
		params: { ...args as any, mode: PageMode.LIST, destroy: 'TODO: is this arg necessary?' },
	});
}

export function openRouteAndReplace(name: string, mode: PageMode, args?: {
	id?: number,
	props?: boolean,
	filter?: object,
	[key: string]: any,
}) {
	return router.replace({
		name: editName(name),
		params: { ...args as any, mode, replace: true },
	});
}

/**
 * Opens new Edit component.
 *
 * @param name - name of the component, eg. 'Vozidlo'.
 * @param mode - page mode
 * @param args
 */
export function openEdit(name: string, mode: PageMode, args?: {
	id?: number,
	props?: boolean,
	filter?: object,
	[key: string]: any,
	defaultAction?: boolean,
}) {
	return router.push({
		name: editName(name),
		params: { ...args as any, mode, destroy: 'TODO: is this arg necessary?' },
	});
}

/**
 * Returns URL of edit component with name `name` and id `id`.
 *
 * @param name
 * @param id
 * @param mode
 */
export function getEditRoute(name: string, id: string | number | null, mode: PageMode = PageMode.EDIT): RawLocation {
	const params = {
		...{
			mode,
			destroy: 'TODO: is this arg necessary?',
		},
		...((id) ? { id: id.toString() } : {}),
	};
	return {
		name: (name.endsWith('Edit')) ? name : editName(name),
		params,
	};
}
